/* global Plaid */
import React, { useCallback, useState } from 'react';
import {
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccess,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link';

import { Button as ConstellationButton, TextButton } from '@zillow/constellation-10';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import { debounce } from '@zg-rentals/zdash';
import { DEBOUNCE_WAIT_TIME } from '@common/constants';
import { logError } from '@zg-rentals/log-error';

interface IPlaidButtonProps {
  plaidEnv: string;
  plaidLinkToken?: string;
  plaidLinkTokenExpiration?: number;
  label: string;
  IconComponent?: any;
  size?: 'sm' | 'md' | 'lg';
  dataTest?: string;
  onSuccess: (plaidToken: Record<string, unknown>) => any;
  onExit: (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => any;
  onClickTracking?: () => void;
  getPlaidLinkToken: () => any;
  useConstellationButton?: boolean;
  full?: boolean;
  loading?: boolean;
  onClickForStorybook?: () => void;
  variant?: 'button' | 'text';
}

const PlaidButton: React.FC<React.PropsWithChildren<IPlaidButtonProps>> = (props: IPlaidButtonProps): JSX.Element => {
  const [token, setToken] = useState<string | null>(props.plaidLinkToken || '');

  const createLinkToken = async () => {
    const response = await props.getPlaidLinkToken();
    setToken(response?.payload?.data?.plaidLinkToken);
  };

  // get link_token from your server when component mounts
  React.useEffect(() => {
    const { plaidEnv, plaidLinkToken, plaidLinkTokenExpiration } = props;
    if (!plaidEnv) {
      throw new Error(`Invalid plaid config: ${plaidEnv}`);
    }
    if (!plaidLinkToken || (plaidLinkTokenExpiration && plaidLinkTokenExpiration < new Date().getTime())) {
      createLinkToken();
    }

    return () => {
      const reset = () => {
        document.body.style.overflow = '';
      };

      reset();
      // reset style again in case Plaid rewrites it
      setTimeout(reset, 0);
    };
  }, []);

  const onSuccess = useCallback<PlaidLinkOnSuccess>((publicToken, metadata) => {
    // send public_token to your server
    // https://plaid.com/docs/api/tokens/#token-exchange-flow

    props.onSuccess({
      publicToken,
      accountId: metadata.accounts[0].id,
      bankName: metadata?.institution?.name || metadata.accounts[0]?.name?.split('.')[0]?.trim(),
      mask: metadata.accounts[0].mask || '',
      status: metadata.accounts[0].verification_status?.toUpperCase() || '',
    });
  }, []);

  const onExit = async (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
    if (error != null && error.error_code === 'INVALID_LINK_TOKEN') {
      createLinkToken();
    }
    props.onExit(error, metadata);
  };

  const config: PlaidLinkOptions = {
    onSuccess,
    onExit,
    token,
  };

  const { open, ready, error } = usePlaidLink(config);

  if (error) {
    logError({ error, context: 'Could not open Plaid window.' });
  }

  const openPlaid = debounce(
    function () {
      if (props.onClickForStorybook) {
        props.onClickForStorybook();
      }
      if (props.onClickTracking) {
        props.onClickTracking();
      }
      open();
    },
    DEBOUNCE_WAIT_TIME,
    true,
  );

  const { label, IconComponent, size, useConstellationButton = false, loading, variant = 'button' } = props;

  if (useConstellationButton) {
    if (variant === 'text') {
      return (
        <TextButton disabled={!ready} onClick={openPlaid} onImpact>
          {label}
        </TextButton>
      );
    }
    return (
      <ConstellationButton
        loading={loading}
        icon={IconComponent}
        emphasis="filled"
        disabled={!ready}
        onClick={openPlaid}
        size={size || 'md'}
        fluid={true}
      >
        {label}
      </ConstellationButton>
    );
  }
  return (
    <Button
      type="primary"
      disabled={!ready}
      onClick={openPlaid}
      icon={IconComponent}
      dataTest={props.dataTest}
      size={size || 'md'}
      full={props.full ?? true}
    >
      {label}
    </Button>
  );
};

export default PlaidButton;
